import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  DirectionsRenderer,
} from "@react-google-maps/api";
import { getGoogleMapsApiKey } from "./config.js";

const libraries = ["places"];
const mapContainerStyle = { width: "100%", height: "600px" };
const defaultCenter = { lat: -17.8274, lng: 31.0435 };

const toNumber = (v) => {
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const toLatLng = (obj) => {
  if (!obj) return null;
  const lat = toNumber(obj.lat ?? obj.latitude);
  const lng = toNumber(obj.lng ?? obj.longitude);
  if (lat == null || lng == null) return null;
  return { lat, lng };
};

const RideSharePassengerJoinRoutePreview = () => {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: getGoogleMapsApiKey(),
    libraries,
  });

  const mapRef = useRef(null);

  const [origin, setOrigin] = useState(null);
  const [destination, setDestination] = useState(null);
  const [directions, setDirections] = useState(null);
  const [eta, setEta] = useState("");

  const center = useMemo(() => origin || destination || defaultCenter, [origin, destination]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    const originLat = toNumber(params.get("origin_lat"));
    const originLng = toNumber(params.get("origin_lng"));
    const destinationLat = toNumber(params.get("destination_lat"));
    const destinationLng = toNumber(params.get("destination_lng"));

    if (originLat != null && originLng != null) setOrigin({ lat: originLat, lng: originLng });
    if (destinationLat != null && destinationLng != null) setDestination({ lat: destinationLat, lng: destinationLng });
  }, []);

  useEffect(() => {
    const handleMessage = (event) => {
      try {
        const raw = event?.data;
        if (!raw) return;
        const payload = typeof raw === "string" ? JSON.parse(raw) : raw;

        const prd = payload?.passengerRouteData ?? payload;

        const nextOrigin = toLatLng(prd?.origin);
        const nextDestination = toLatLng(prd?.dest);

        if (nextOrigin) setOrigin(nextOrigin);
        if (nextDestination) setDestination(nextDestination);
      } catch (e) {
        return;
      }
    };

    window.addEventListener("message", handleMessage);
    document.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
      document.removeEventListener("message", handleMessage);
    };
  }, []);

  useEffect(() => {
    if (!isLoaded || !window.google) return;
    if (!origin || !destination) return;

    const service = new window.google.maps.DirectionsService();

    service.route(
      {
        origin,
        destination,
        travelMode: window.google.maps.TravelMode.DRIVING,
        provideRouteAlternatives: false,
        optimizeWaypoints: false,
      },
      (result, status) => {
        if (status === "OK") {
          setDirections(result);
          const totalSeconds = result.routes[0].legs.reduce((sum, leg) => sum + leg.duration.value, 0);
          setEta(`${Math.ceil(totalSeconds / 60)} min`);
        } else {
          setDirections(null);
          setEta("");
        }
      }
    );
  }, [isLoaded, origin, destination]);

  if (loadError) return <div>Error loading Google Maps</div>;
  if (!isLoaded) return <div>Loading map...</div>;

  return (
    <div style={{ maxWidth: "900px", margin: "auto" }}>
      <h2>Passenger Join Route Preview</h2>

      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        zoom={12}
        center={center}
        onLoad={(map) => (mapRef.current = map)}
      >
        {origin && <Marker position={origin} title="Origin" label="O" />}
        {destination && <Marker position={destination} title="Destination" label="D" />}

        {directions && (
          <DirectionsRenderer
            directions={directions}
            options={{
              suppressMarkers: true,
              polylineOptions: {
                strokeColor: "#9B59B6",
                strokeWeight: 5,
                strokeOpacity: 0.85,
              },
              preserveViewport: false,
              draggable: false,
            }}
          />
        )}
      </GoogleMap>

      <div style={{ marginTop: "10px" }}>
        <p>
          <strong>ETA:</strong> {eta || "N/A"}
        </p>
      </div>
    </div>
  );
};

export default RideSharePassengerJoinRoutePreview;
